/**
 * src/admin/permissions.js
 *
 * Shared permission utilities for the admin panel.
 *
 * IMPORTANT — two ways to get the current admin's permissions:
 *
 * 1. `usePermissions()` — reads PermissionsContext. Only works correctly in
 *    components rendered INSIDE <AdminLayout>'s children (e.g. a sub-component
 *    like ApplicationViewer that AdminLayout mounts as part of its own tree).
 *    It will NOT work in the top-level page component that itself renders
 *    <AdminLayout>...</AdminLayout> — that component is AdminLayout's PARENT,
 *    not its descendant, so the Provider (which lives inside AdminLayout)
 *    hasn't wrapped it yet when its hooks run.
 *
 * 2. `useCurrentAdmin()` — fetches the admin directly via authAPI.me(), with
 *    a simple in-memory cache so multiple components calling it don't cause
 *    duplicate network requests. Safe to call from ANY component, regardless
 *    of position in the tree. Use this in top-level page components (like
 *    AdminDashboard) that need permission checks before/outside AdminLayout.
 */
import { createContext, useContext, useState, useEffect } from "react";
import { authAPI } from "../services/api";

export const PermissionsContext = createContext({
  role: null,
  permissions: null, // null = super_admin (all access) | {} / {key:true} = regular admin
  product: null,     // "forge" | "makua" — which backend this admin is pointed at
  loading: true,
});

export const usePermissions = () => useContext(PermissionsContext);

/**
 * true if this deployment uses `feature` at all.
 *
 * Separate from permissions: a permission is about who this person is, a
 * feature is about what this instance is for. Forge has no workshops
 * tables, so no role should be offered that screen; Makua has no
 * applications, so it shouldn't be offered Applications.
 *
 * An API that doesn't report features (or a feature not in the map) is
 * treated as on, so the panel degrades to its old behaviour.
 */
export function hasFeature(admin, feature) {
  if (!feature) return true;
  if (!admin || !admin.features) return true;
  return admin.features[feature] !== false;
}

/** true if this admin can access `key`. Super admins (role === "super_admin") always pass. */
export function hasPermission(admin, key) {
  if (!admin) return false;
  if (admin.role === "super_admin") return true;
  if (!admin.permissions) return false;
  return admin.permissions[key] === true;
}

/* ── Standalone admin fetch — works anywhere, not dependent on Context ── */
let _adminCache = null;
let _adminPromise = null;

/** Clear the cache — call this on logout or when permissions might have changed server-side. */
export function clearAdminCache() {
  _adminCache = null;
  _adminPromise = null;
}

export function useCurrentAdmin() {
  const [admin, setAdmin] = useState(_adminCache);
  const [loading, setLoading] = useState(!_adminCache);

  useEffect(() => {
    let alive = true;

    if (_adminCache) {
      setAdmin(_adminCache);
      setLoading(false);
      return;
    }

    if (!_adminPromise) {
      _adminPromise = authAPI
        .me()
        .then((res) => {
          if (res?.data) {
            const a = res.data;
            _adminCache = {
              id: a.id,
              name: a.name,
              email: a.email,
              role: a.role, // raw: "admin" | "super_admin"
              roleLabel: a.role === "super_admin" ? "Super Admin" : a.role_name || "Admin",
              permissions: a.permissions, // null = super_admin, else { key: true, ... }
              product: a.product || null,
              brand: a.brand || null,
              // Which screens this deployment uses. Missing (an older API)
              // means "show everything", so the panel never goes blank.
              features: a.features || null,
            };
          }
          return _adminCache;
        })
        .catch(() => null);
    }

    _adminPromise.then((result) => {
      if (alive) {
        setAdmin(result);
        setLoading(false);
      }
    });

    return () => {
      alive = false;
    };
  }, []);

  return { admin, loading };
}

/** Nav item -> permission key required to see it. Dashboard has no gate. */
export const NAV_PERMISSION_MAP = {
  "/admin/applications": "applications.view",
  "/admin/subscribers": "subscribers.view",
  "/admin/donations": "donations.view",
  "/admin/notifications": "notifications.view",
  "/admin/stories": "stories.view",
  "/admin/courses": "courses.view",
  "/admin/settings": "settings.view",
  "/admin/queue": "queue.view",
  "/admin/retreat-blocks": "blocks.view",
  "/admin/bookings": "blocks.view",
  "/admin/cohorts": "cohorts.view",
  "/admin/rooms": "blocks.view",
  "/admin/workshops": "workshops.view",
  "/admin/products": "shop.view",
  "/admin/orders": "shop.view",
  "/admin/board": "board.view",
  "/admin/resources": "resources.view",
  // /admin/roles is super_admin only — handled separately, not by permission key
};
